<template>
<Form
	ref="form"
	class="m-0 p-0 g-0"
	@submit="onSubmit"
	@invalid-submit="console.log"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ isSubmitting, values, errors, meta }"
>
<fieldset :disabled="!isFormEnabled">
	<div style="display: none;">
		{{ formMeta = meta }}
		{{ isSubmittingForm = isSubmitting }}
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-1">
			<label class="fw-bold">
				Первинні налаштування
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.unitVersion] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.unitVersion"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.unitVersion] }"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option
					v-for="version in Object.values(ServiceUnit.versions)"
					:key="version"
					:value="version"
				>
					{{ ServiceUnit.versionNames[version] }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.unitVersion] }}
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic mb-0">
					Версія складається з двох частин розділених дефісом.<br>
					1. Рік випуску.<br>
					2. Канал на якому буде вимірюватись потенціал з електрода порівняння.<br>
				</p>
				<p class="form-text fst-italic paragraph-indent">
					Версія 2025-3 аналогічна 2025-1, за виключенням охоронних контурів: стан "аварія" інтерпретується як "норма".
				</p>
			</div>
		</div>
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 col-xl-6">
			<label>{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.unitPort] }}</label>
			<Field
				:name="Station.passportKeys.serviceUnit.unitPort"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.unitPort] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.unitPort] }}
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Якщо порт співпадає з налаштуваннями модема "{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.server1Port1] }}" або 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.server2Port1] }}", 
					то для опитування модуля застосовується протокол відповідно до налаштування відповідного порта модема 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.convertingRtu1] }}" або 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.convertingRtu2] }}".<br>
				</p>
				<p class="form-text fst-italic paragraph-indent">
					Якщо порт не співпадає, то протокол ModBus TCP.
				</p>
			</div>
		</div>
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 col-xl-6">
			<label>{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.unitModbusAddr] }}</label>
			<Field
				:name="Station.passportKeys.serviceUnit.unitModbusAddr"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.unitModbusAddr] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.unitModbusAddr] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<a :id="sections.clock.anchor" class="anchor-shift" />
	<div ref="clockRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Годинник
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<Field
				:name="$options.CLOCK_KEY"
				type="datetime-local"
				class="form-control form-control-sm no-arrow"
				:class="{
					'is-invalid': errors[$options.CLOCK_KEY]
				}"
				@change.stop="
					onChangeDate(
						$options.CLOCK_KEY,
						$event.target.value
					)
				"
				disabled
			/>
			<div class="invalid-feedback">
				{{ errors[$options.CLOCK_KEY] }}
			</div>

			<div class="text-nowrap mt-1">
				<Field
					:name="Station.passportKeys.serviceUnit.clockSynchronization"
					type="checkbox"
					id="clockSynchronization"
					class="form-check-input col-auto"
					:value="true"
					:uncheckedValue="false"
				/>
				<label for="clockSynchronization" class="form-check-label text-wrap mx-2 my-0 col-auto">
					{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.clockSynchronization] }}
				</label>
			</div>
		</div>

		<div class="col-12 col-xl-6 mt-1">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Значення часу покзано на момент зв'язку з модулем.<br>
					В модулі годинник продовжує хід.<br>
					<!-- Встановлене в полі значення буде записано після натиснення на кнопку "Записати". -->
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.measurePotentialOnOffStation.anchor" class="anchor-shift" />
	<div ref="measurePotentialOnOffStationRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				{{ Station.settingNames[Station.passportKeys.measurePotentialOnOffStation.self].self }}
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>
				{{ Station.settingNames[Station.passportKeys.measurePotentialOnOffStation.self][Station.passportKeys.measurePotentialOnOffStation.isEnable] }}
			</label>
			<div
				class="row row-cols-2 mb-2 mt-0 ms-0 btn-group"
				style="min-width: 15rem; width: 100%;"
				role="group"
			>
				<Field
					type="radio"
					class="btn-check"
					:class="{ 'is-invalid': errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.isEnable}`] }"
					:name="`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.isEnable}`"
					:value="true"
					id="is_enable-yes"
				/>
				<label class="mt-0 btn btn-sm btn-outline-secondary" for="is_enable-yes">
					так
				</label>

				<Field
					type="radio"
					class="btn-check"
					:class="{ 'is-invalid': errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.isEnable}`] }"
					:name="`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.isEnable}`"
					:value="false"
					id="is_enable-no"
				/>
				<label class="mt-0 btn btn-sm btn-outline-secondary" for="is_enable-no">
					ні
				</label>
			</div>
			<div class="invalid-feedback">
				{{ errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.isEnable}`] }}
			</div>

			<label class="mt-2">
				{{ Station.settingNames[Station.passportKeys.measurePotentialOnOffStation.self][Station.passportKeys.measurePotentialOnOffStation.period] }}
			</label>
			<Field
				:name="`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.period}`"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.period}`] }"
			/>
			<div class="invalid-feedback">
				{{ errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.period}`] }}
			</div>

			<label class="mt-3">
				{{ Station.settingNames[Station.passportKeys.measurePotentialOnOffStation.self][Station.passportKeys.measurePotentialOnOffStation.startTime] }}
			</label>
			<Field
				:name="`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.startTime}`"
				type="time"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.startTime}`] }"
			/>
			<div class="invalid-feedback">
				{{ errors[`${Station.passportKeys.measurePotentialOnOffStation.self}.${Station.passportKeys.measurePotentialOnOffStation.startTime}`] }}
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Вимірювання потенціалу на вимкненній СКЗ - фукція періодичного автоматичного вимірювання потенціалу на електродах при вимкненому силовому модулі.
					Вимірювання виконується з заданым періодом в заданий час доби.
				</p>
				<ol class="list-group list-group-flush">
					<span class="form-text fst-italic fw-bold">Алгоритм вимірювання</span>
					<li class="list-group-item">
						<span class="form-text fst-italic text-break">
							Вимикається подача струму на електроди
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic text-break">
							10 хвилин стабілізується потенціал
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic text-break">
							Усереднений потенціал зчитується з сервісного модуля
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic text-break">
							Силовий модуль повертається до робочого режиму
						</span>
					</li>
				</ol>
				<p class="form-text fst-italic paragraph-indent">
					Кожне автоматичне вимірювання буде зберігатись як запит в розділі "Телекерування".
					Результати вимірювання можна переглянути в розділі "Телеконтроль" в відповідній плитці.
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.power220.anchor" class="anchor-shift" />
	<div ref="power220Ref" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Живлення 220В
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.u220Min] }}</label>
			<Field
				:name="Station.passportKeys.serviceUnit.u220Min"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.u220Min] }"
			/>
			<div class="invalid-feedback">{{ errors[Station.passportKeys.serviceUnit.u220Min] }}</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.u220Min)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.u220Min)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.u220Max] }}</label>
			<Field
				:name="Station.passportKeys.serviceUnit.u220Max"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.u220Max] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.u220Max] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.u220Max)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.u220Max)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.u220ThresholdHandler] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.u220ThresholdHandler"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.u220ThresholdHandler] }"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option v-for="handler in ServiceUnit.channelTrasholdHandlers" :key="handler.code" :value="handler.code">
					{{ handler.title }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.u220ThresholdHandler] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.u220ThresholdHandler)"
			>
				Поточне значення регістра: 
				{{ 
					ServiceUnit.channelTrasholdHandlers
						.find((el) => el.code == station.getMetric(Station.passportKeys.serviceUnit.u220ThresholdHandler)?.value?.toFixed(2))
						?.title
				}}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.u220ShiftA] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.u220ShiftA"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.u220ShiftA] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.u220ShiftA] }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.u220ShiftB] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.u220ShiftB"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.u220ShiftB] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.u220ShiftB] }}
			</div>

			<div class="form-group mt-2" >
				<Field
					:name="`${Station.passportKeys.notificationDistributionLists.self}.${Station.passportKeys.notificationDistributionLists.u220}`"
					type="checkbox"
					class="form-check-input"
					:value="true"
					:uncheckedValue="false"
				/>
				Сповіщати
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Канал, що вимірює живлення станції 220 Вольт.<br>
					Налаштовуються мінімальний і максимальний аварійні пороги.<br>
					Реакція на перетин порогових значень.<br>
					Коефіцієнти коригування вимірювань канала.<br>
					Сповіщення про події в каналі в додаток для мобільного телефону.
					Для того щоб отримувати сповіщення, необхідно обрати реакцію на вихід за межі і активувати отримання сповіщень.
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.channel1.anchor" class="anchor-shift" />
	<div ref="channel1Ref" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Канал 1
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel1InpMin] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel1InpMin"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel1InpMin] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel1InpMin] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel1InpMin)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.channel1InpMin)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel1InpMax] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel1InpMax"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel1InpMax] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel1InpMax] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel1InpMax)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.channel1InpMax)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel1ThresholdHandler] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel1ThresholdHandler"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel1ThresholdHandler] }"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option v-for="handler in ServiceUnit.channelTrasholdHandlers" :key="handler.code" :value="handler.code">
					{{ handler.title }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel1ThresholdHandler] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel1ThresholdHandler)"
			>
				Поточне значення регістра: 
				{{ 
					ServiceUnit.channelTrasholdHandlers
						.find((el) => el.code == station.getMetric(Station.passportKeys.serviceUnit.channel1ThresholdHandler)?.value?.toFixed(2))
						?.title
				}}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel1ShiftA] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.channel1ShiftA"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.channel1ShiftA] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.channel1ShiftA] }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel1ShiftB] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.channel1ShiftB"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.channel1ShiftB] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.channel1ShiftB] }}
			</div>

			<div class="form-group mt-2">
				<Field
					:name="`${Station.passportKeys.notificationDistributionLists.self}.${Station.passportKeys.notificationDistributionLists.channel_1}`"
					type="checkbox"
					class="form-check-input"
					:value="true"
					:uncheckedValue="false"
				/>
				Сповіщати
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Канал 1 для вимірювання довільного потенціалу.<br>
					Налаштовуються мінімальний і максимальний аварійні пороги.<br>
					Реакція на перетин порогових значень.<br>
					Коефіцієнти коригування вимірювань канала.<br>
					Сповіщення про події в каналі в додаток для мобільного телефону.
					Для того щоб отримувати сповіщення, необхідно обрати реакцію на вихід за межі і активувати отримання сповіщень.
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.channel2.anchor" class="anchor-shift" />
	<div ref="channel2Ref" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Канал 2
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel2InpMin] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel2InpMin"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel2InpMin] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel2InpMin] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel2InpMin)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.channel2InpMin)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel2InpMax] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel2InpMax"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel2InpMax] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel2InpMax] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel2InpMax)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.channel2InpMax)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel2ThresholdHandler] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.channel2ThresholdHandler"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.channel2ThresholdHandler] }"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option v-for="handler in ServiceUnit.channelTrasholdHandlers" :key="handler.code" :value="handler.code">
					{{ handler.title }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.channel2ThresholdHandler] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.channel2ThresholdHandler)"
			>
				Поточне значення регістра: 
				{{ 
					ServiceUnit.channelTrasholdHandlers
						.find((el) => el.code == station.getMetric(Station.passportKeys.serviceUnit.channel2ThresholdHandler)?.value?.toFixed(2))
						?.title
				}}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel2ShiftA] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.channel2ShiftA"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.channel2ShiftA] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.channel2ShiftA] }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.channel2ShiftB] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.channel2ShiftB"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.channel2ShiftB] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.channel2ShiftB] }}
			</div>

			<div class="form-group mt-2" >
				<Field
					:name="`${Station.passportKeys.notificationDistributionLists.self}.${Station.passportKeys.notificationDistributionLists.channel_2}`"
					type="checkbox"
					class="form-check-input"
					:value="true"
					:uncheckedValue="false"
				/>
				Сповіщати
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Канал 2 для вимірювання довільного потенціалу.<br>
					Налаштовуються мінімальний і максимальний аварійні пороги.<br>
					Реакція на перетин порогових значень.<br>
					Коефіцієнти коригування вимірювань канала.<br>
					Сповіщення про події в каналі в додаток для мобільного телефону.
					Для того щоб отримувати сповіщення, необхідно обрати реакцію на вихід за межі і активувати отримання сповіщень.
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.shunt.anchor" class="anchor-shift" />
	<div ref="shuntRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Шунт
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label>
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.shuntMin] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.shuntMin"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.shuntMin] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.shuntMin] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.shuntMin)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.shuntMin)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.shuntMax] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.shuntMax"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.shuntMax] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.shuntMax] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.shuntMax)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.shuntMax)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.shuntThresholdHandler] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.shuntThresholdHandler"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.shuntThresholdHandler] }"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option v-for="handler in ServiceUnit.channelTrasholdHandlers" :key="handler.code" :value="handler.code">
					{{ handler.title }}
				</option>
			</Field>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.shuntThresholdHandler] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.shuntThresholdHandler)"
			>
				Поточне значення регістра: 
				{{ 
					ServiceUnit.channelTrasholdHandlers
						.find((el) => el.code == station.getMetric(Station.passportKeys.serviceUnit.shuntThresholdHandler)?.value?.toFixed(2))
						?.title
				}}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.shuntShiftA] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.shuntShiftA"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.shuntShiftA] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.shuntShiftA] }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.shuntShiftB] }}
			</label>
			<Field
				:name="ServiceUnit.passportKeys.shuntShiftB"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[ServiceUnit.passportKeys.shuntShiftB] }"
			/>
			<div class="invalid-feedback">
				{{ errors[ServiceUnit.passportKeys.shuntShiftB] }}
			</div>

			<div class="form-group mt-2">
				<Field
					:name="`${Station.passportKeys.notificationDistributionLists.self}.${Station.passportKeys.notificationDistributionLists.shunt}`"
					type="checkbox"
					class="form-check-input"
					:value="true"
					:uncheckedValue="false"
				/>
				Сповіщати
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Канал шунта струму на електрод.<br>
					Налаштовуються мінімальний і максимальний аварійні пороги.<br>
					Реакція на перетин порогових значень.<br>
					Коефіцієнти коригування вимірювань канала.<br>
					Сповіщення про події в каналі в додаток для мобільного телефону.
					Для того щоб отримувати сповіщення, необхідно обрати реакцію на вихід за межі і активувати отримання сповіщень.
				</p>
			</div>
		</div>
	</div>

	<a v-if="showSecurityCircuits" :id="sections.securityCircuits.anchor" class="anchor-shift" />
	<div
		ref="securityCircuitsRef"
		v-if="showSecurityCircuits"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Охоронні контури
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<template
				v-for="(circuit, index) in station.securityCircuitsNotificationNames"
				:key="index + 1"
			>
				<label class="fw-bold mt-2">Контур {{ index + 1 }}</label><br>
				<label class="">
					{{ ServiceUnit.settingNames[ServiceUnit.passportKeys[`securityCircuit_${index + 1}_purpose`]] }}
				</label>
				<Field
					:name="`security_circuit_${index + 1}_purpose`"
					as="select"
					class="form-control form-control-sm"
					:class="{ 'is-invalid': errors[`security_circuit_${index + 1}_purpose`] }"
				>
					<option value="" disabled>Натисніть щоб обрати</option>
					<option
						v-for="[key, name] in Object.entries(ServiceUnit.securityCircuitPurposeNames)"
						:key="key"
						:value="key"
					>
						{{ name }}
					</option>
				</Field>
				<div class="invalid-feedback">
					{{ errors[`security_circuit_${index + 1}_purpose`] }}
				</div>
					
				<label class="mt-2">
					{{ ServiceUnit.settingNames[ServiceUnit.passportKeys[`securityCircuit_${index + 1}_handler`]] }}
				</label>
				<Field
					:name="`security_circuit_${index + 1}_handler`"
					as="select"
					class="form-control form-control-sm"
					:class="{ 'is-invalid': errors[`security_circuit_${index + 1}_handler`] }"
				>
					<option value="" disabled>Натисніть щоб обрати</option>
					<option
						v-for="handler in ServiceUnit.securityCircuitHandlers"
						:key="handler.code"
						:value="handler.code"
					>
						{{ handler.title }}
					</option>
				</Field>
				<div class="invalid-feedback">
					{{ errors[`security_circuit_${index + 1}_handler`] }}
				</div>
				<div
					class="fst-italic cur-value-promt"
					:class="fieldDynamicClasses(values, errors, `security_circuit_${index + 1}_handler`)"
				>
					Поточне значення регістра: 
					{{ 
						ServiceUnit.securityCircuitHandlers
							.find((el) => el.code == station.getMetric(`security_circuit_${index + 1}_handler`)?.value?.toFixed(2))
							?.title
					}}
				</div>

				<div class="form-group mt-1">
					<Field
						:name="`${Station.passportKeys.notificationDistributionLists.self}.${circuit}`"
						type="checkbox"
						class="form-check-input"
						:value="true"
						:uncheckedValue="false"
					/>
					Сповіщати
				</div>
			</template>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Охоронні контури модуля.<br>
					Для того, щоб отримувати сповіщення, необхідно обрати призначення і реакцію.
					На кожне призначення можна обирати тільки один контур.<br>
					Поточний стан контурів у яких обрано призначення можна спостерігати в розділі "Телекерування" плитка "Стан станції".<br>
					Стан контура з призначенням "{{ ServiceUnit.securityCircuitPurposeNames.box_door }}" можна спостерігати в розділі "Охорона".
				</p>
			</div>
		</div>
	</div>

	<a :id="sections.impulseCounter.anchor" class="anchor-shift" />
	<div ref="impulseCounterRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Лічильник імпульсів електролічильника
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.pwrCounterK] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.pwrCounterK"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.pwrCounterK] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.pwrCounterK] }}
			</div>

			<label class="mt-2">Показники лічильника єлектроенергії, кВт⋅год</label>
			<!-- <Field
				v-model="pwrCounterValue"
				:name="`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.pwrCntStart}`"
				type="number"
				class="form-control form-control-sm no-arrow"
				:disabled="true"
			/> -->
			<input
				:value="pwrCounterValue"
				type="number"
				class="form-control form-control-sm no-arrow"
				disabled
			/>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Для розрахунку показань спожитої енергії в КВт/год, імпульси лічильника сервісного модуля діляться на вказаний коефіцієнт.<br>
					Для використання показань саме сервісного модуля необхіно обрати відповідний тип в секції "{{ Station.unitNames[Station.passportKeys.electricMeter.self] }}" що знаходиться нижче.<br>
				</p>
				<p class="form-text fst-italic paragraph-indent">
					Поточний розрахований з використанням збереженого коефіцієнта показник виводиться в полі "Показники лічильника єлектроенергії".
				</p>
			</div>
		</div>
	</div>

	<a v-if="showAkkumulatorSection" :id="sections.akkumulator.anchor" class="anchor-shift" />
	<!-- Show only if service unit support akkumulator settings -->
	<div
		ref="akkumulatorRef"
		v-if="showAkkumulatorSection"
		class="form-row row m-0 p-0 g-3"
	>
		<div class="col-12 mt-4">
			<label class="fw-bold">
				Вимірювання напруги на акумуляторі
			</label>
		</div>
		
		<div class="col-12 col-xl-6 mt-1">
			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.akkEmptyVoltage] }}
			</label>
			<Field
				:name="Station.passportKeys.serviceUnit.akkEmptyVoltage"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.akkEmptyVoltage] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.serviceUnit.akkEmptyVoltage] }}
			</div>
			<div
				class="fst-italic cur-value-promt"
				:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.akkEmptyVoltage)"
			>
				Поточне значення регістра: 
				{{ station.getMetric(Station.passportKeys.serviceUnit.akkEmptyVoltage)?.value?.toFixed(2) }}
			</div>

			<label class="mt-2">
				{{ ServiceUnit.settingNames[ServiceUnit.passportKeys.akkFullVoltage] }}
			</label>
				<Field
					:name="Station.passportKeys.serviceUnit.akkFullVoltage"
					type="number"
					class="form-control form-control-sm no-arrow"
					:class="{ 'is-invalid': errors[Station.passportKeys.serviceUnit.akkFullVoltage] }"
				/>
				<div class="invalid-feedback">
					{{ errors[Station.passportKeys.serviceUnit.akkFullVoltage] }}
				</div>
				<div
					class="fst-italic cur-value-promt"
					:class="fieldDynamicClasses(values, errors, Station.passportKeys.serviceUnit.akkFullVoltage)"
				>
					Поточне значення регістра: 
					{{ station.getMetric(Station.passportKeys.serviceUnit.akkFullVoltage)?.value?.toFixed(2) }}
				</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<div class="form-row row row-cols-1 m-0 p-0 mt-2">
		<!-- {{ console.log('relatedRpc', relatedRpc) }} -->
		<dl v-if="relatedRpc" class="mb-1 text-dark">
			<dt>Останній запит запису регістрів</dt>
			<dd class="mb-0 ms-3">
				Відправив: <span class="font-monospace">{{ relatedRpc.context?.writer?.name }}</span>
				<span v-if="relatedRpc.context?.writer?.organization?.name" class="font-monospace">
					&nbsp;({{ relatedRpc.context?.writer?.organization?.name }})
				</span>
			</dd>
			<dd class="mb-0 ms-3">
				Час: 
				<span class="font-monospace">
					{{ dateTimeToString(relatedRpc[rpcTaskSchema.updateTime.key], false, false, true) }}
				</span>
			</dd>
			<dd class="mb-0 ms-3">
				Стан: <span
					class="font-monospace"
					:class="{
						'text-danger': rpcTaskSchema.state.isErorrState(relatedRpc?.state),
						'text-success': !rpcTaskSchema.state.isErorrState(relatedRpc?.state)
					}"
				>
					{{ taskStateDescription(relatedRpc?.state) }}
				</span>
			</dd>
			<!-- {{relatedRpc?._id}} -->
		</dl>
	</div>


	<div class="row row-cols-1 m-0 p-0 my-3">
		<div class="col-12 my-0">
			<p v-if="feedbackPromt.length > 0" class="m-0" :class="feedbackPromtClass">
				{{ feedbackPromt }}
			</p>
			<p v-else-if="meta.dirty && meta.valid" class="text-success m-0">
				Натисніть "Зберегти і записати регістри" щоб примінити налаштування і записати зміни в регістри модулів.
			</p>
			<p v-else-if="meta.dirty && !meta.valid" class="text-danger m-0">
				Є поля що заповнені некоректно.
			</p>
			<p v-else class="m-0">&nbsp;</p>
		</div>
	</div>

	<div class="row row-cols-1 form-row m-0 p-0 mb-2">
		<div class="col-12 my-0 mt-1">
			<button
				ref="submitButtonRef"
				type="submit"
				class="btn btn-primary btn-sm"
				:disabled="!meta.valid || !meta.dirty || isSubmitting"
			>
				Записати
			</button>
		</div>
	</div>
</fieldset>
</Form>
</template>


<script>
import { useStationsStore } from '@/stores/StationsStore.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { rpcTaskSchema, useRpcTasks, } from '@/composables/rpcTasks.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate'
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';

import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { ModemUnit } from '@/models/modemUnit.js'
import {
	parseApiTimeDate,
	convertToApiTimeDate,
} from '@/api/api.js'
import {
	capitalizeFirstLetter,
	dateTimeToString,
	getObjectDiff,
	dateTimeLocalString,
} from '@/helpers.js'


export default {
	requiredPromt: 'Обов\'язкове поле',
	overMinLenPromt: 'Довжина меньше мінімальної',
	overMaxLenPromt: 'Довжина більше максимальної',
	overMinPromt: 'Значення меньше мінімального',
	overMaxPromt: 'Значення більше максимального',
	onlyIntegersPromt: 'Має бути цілим числом',
	onlyPositivePromt: 'Має бути натуральним числом',
	wrongInputPromt: 'Не відповідає вимогам',

	inject: ['headerHeight'],

	CLOCK_KEY: 'clock-timedate',

	components: {
		Form,
        Field,
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},
	},

	emits: {
		rpc: (promt) => typeof promt === 'object',
		sections: (s) => typeof s === "object",
		saveFunction: (f) => typeof f === 'function',
		saveFunctionActive: (f) => typeof f === 'boolean',
	},

	setup() {
		const stationsStore = useStationsStore()
		const accountStore = useAccontStore()
		const { createRpc, readRpc, } = useRpcTasks()

		return {
			stationsStore,
			accountStore,
			createRpc,
			readRpc,
			Station,
			ServiceUnit,
			ModemUnit,
			capitalizeFirstLetter,
			dateTimeToString,
			rpcTaskSchema,
		}
	},

	data() {
		const schema = Yup.object().shape({
			unit_version: Yup.string()
			.typeError(this.$options.wrongInputPromt)
			.required(this.$options.requiredPromt),
			unit_port: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.integer(this.$options.onlyIntegersPromt)
				.min(1, this.$options.overMinPromt)
				.max(65535, this.$options.overMaxPromt)
				// Port is required only when service unit is present
				.when('unit_version', {
					is: ServiceUnit.versions.unknown, // type of unit
					//then: (schema) => schema,
					otherwise: (schema) => schema.required(this.$options.requiredPromt),
				}),
			unit_modbus_addr: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.integer(this.$options.onlyIntegersPromt)
				.min(1, this.$options.overMinPromt)
				.max(255, this.$options.overMaxPromt)
				// ModBus address is required only when service unit is present
				.when('unit_version', {
					is: ServiceUnit.versions.unknown, // type of unit
					//then: (schema) => schema,
					otherwise: (schema) => schema.required(this.$options.requiredPromt),
				}),

			pwr_counter_k: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.min(0, this.$options.overMinPromt)
				.max(65535, this.$options.overMaxPromt),

			[Station.passportKeys.serviceUnit.akkEmptyVoltage]: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.min(5, this.$options.overMinPromt)
				.max(20, this.$options.overMaxPromt),
			[Station.passportKeys.serviceUnit.akkFullVoltage]: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.min(10, this.$options.overMinPromt)
				.max(30, this.$options.overMaxPromt),
			
			u220_min: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(100, this.$options.overMinPromt)
				.max(219, this.$options.overMaxPromt),
			u220_max: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(221, this.$options.overMinPromt)
				.max(300, this.$options.overMaxPromt),
			u220_threshold_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(6, this.$options.overMaxPromt),
			u220_shift_A: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			u220_shift_B: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			channel_1_inp_min: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			channel_1_inp_max: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			channel_1_threshold_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(6, this.$options.overMaxPromt),
			channel_1_shift_A: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			channel_1_shift_B: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			channel_2_inp_min: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			channel_2_inp_max: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			channel_2_threshold_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(6, this.$options.overMaxPromt),
			channel_2_shift_A: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			channel_2_shift_B: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			shunt_min: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			shunt_max: Yup.number()
				.min(-99, this.$options.overMinPromt)
				.max(99, this.$options.overMaxPromt),
			shunt_threshold_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(6, this.$options.overMaxPromt),
			shunt_shift_A: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			shunt_shift_B: Yup.number()
				.min(-1000000, this.$options.overMinPromt)
				.max(1000000, this.$options.overMaxPromt),
			security_circuit_1_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_2_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_3_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_4_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_5_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_6_handler: Yup.number()
				.integer(this.$options.onlyIntegersPromt)
				.min(0, this.$options.overMinPromt)
				.max(4, this.$options.overMaxPromt),
			security_circuit_1_purpose: Yup.string(),
			security_circuit_2_purpose: Yup.string(),
			security_circuit_3_purpose: Yup.string(),
			security_circuit_4_purpose: Yup.string(),
			security_circuit_5_purpose: Yup.string(),
			security_circuit_6_purpose: Yup.string(),

			measure_potential_on_off_station: Yup.object().shape({
				is_enable: Yup.boolean()
					.default(false),
				period: Yup.number()
					.when('is_enable', {
						is: true,
						then: (schema) => schema.required(this.$options.requiredPromt),
					})
					// checking self-equality works for NaN, transforming it to null
					//.transform((_, val) => val === Number(val) ? 100 : 10) 
					.typeError(this.$options.wrongInputPromt)
					.integer(this.$options.onlyIntegersPromt)
					.positive(this.$options.onlyPositivePromt)
					.min(1, this.$options.overMinPromt)
					.max(265, this.$options.overMaxPromt),
					//.nullable(true)
					//.default(10),
				start_time: Yup.string()
					.when('is_enable', {
						is: true,
						then: (schema) => schema.required(this.$options.requiredPromt),
					})
					// .test("is-valid", this.$options.wrongInputPromt, function(value) {
					// 	console.log(value, value?.slice(0,2), value?.slice(3,5))
					// 	const time = new Date(1900, 0, 1, value?.slice(0,2), value?.slice(3,5))
					// 	console.log(time)
					// 	return time !== undefined
					// })
					.typeError(this.$options.wrongInputPromt),
					//.default("00:10"),
			}),
		})

		return {
			// Form
			schema,
			formMeta: {},
			isSubmittingForm: false,

			relatedRpc: undefined,
			rpcUpdateInterval: null,

			observer: null, // IntersectionObserver

			sections: {
				clock: {
					anchor: 'clock',
					title: 'Годинник',
					intersectionRatio: 0, // 0 .. 1
				},
				measurePotentialOnOffStation: {
					anchor: 'measure-potential-on-off-station',
					title: 'Потенц. на вимк. СКЗ',
					intersectionRatio: 0, // 0 .. 1
				},
				power220: {
					anchor: 'power-220',
					title: 'Живлення 220В',
					intersectionRatio: 0, // 0 .. 1
				},
				channel1: {
					anchor: 'channel-1',
					title: 'Канал 1',
					intersectionRatio: 0, // 0 .. 1
				},
				channel2: {
					anchor: 'channel-2',
					title: 'Канал 2',
					intersectionRatio: 0, // 0 .. 1
				},
				shunt: {
					anchor: 'shunt',
					title: 'Шунт',
					intersectionRatio: 0, // 0 .. 1
				},
				securityCircuits: {
					anchor: 'security-circuits',
					title: 'Охоронні контури',
					intersectionRatio: 0, // 0 .. 1
				},
				impulseCounter: {
					anchor: 'impulse-counter',
					title: 'Лічильник імпульсів',
					intersectionRatio: 0, // 0 .. 1
				},
				akkumulator: {
					anchor: 'akkumulator',
					title: 'Акумулятор',
					intersectionRatio: 0, // 0 .. 1
				},
			},

			// Feedback promt
			feedbackPromtClass: '',
			feedbackPromt: '',
		}
	},

	watch: {
		formMeta(newMeta) {
			if (newMeta) {
				this.$emit('saveFunctionActive', newMeta.valid && newMeta.dirty && !this.isSubmittingForm)
				// console.log('meta change', newMeta, newMeta.valid && newMeta.dirty && !this.isSubmittingForm)
			}
		},
	},

	created() {
		this.readRelatedRpc()
	},

	mounted() {
		// Emit function
		this.$emit('saveFunction', this.submitButtonClick)

		// Add observer for viewport area feedback
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// console.log(`isIntersecting = ${entry.isIntersecting} intersectionRatio = ${entry.intersectionRatio}`)
					switch (entry.target) {
						case this.$refs.clockRef:
							this.sections.clock.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.measurePotentialOnOffStationRef:
							this.sections.measurePotentialOnOffStation.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.power220Ref:
							this.sections.power220.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.channel1Ref:
							this.sections.channel1.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.channel2Ref:
							this.sections.channel2.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.shuntRef:
							this.sections.shunt.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.securityCircuitsRef:
							this.sections.securityCircuits.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.impulseCounterRef:
							this.sections.impulseCounter.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.akkumulatorRef:
							this.sections.akkumulator.intersectionRatio = entry.intersectionRatio
							break
					}
				})
			},
			{
				root: null, // null means all viewport
				rootMargin: '0px',
				threshold: [0, 0.5, 1] // callback call trasholds 0%, 50% and 100% viewport
			}
		)

		this.observer.observe(this.$refs.clockRef)
		this.observer.observe(this.$refs.measurePotentialOnOffStationRef)
		this.observer.observe(this.$refs.power220Ref)
		this.observer.observe(this.$refs.channel1Ref)
		this.observer.observe(this.$refs.channel2Ref)
		this.observer.observe(this.$refs.shuntRef)
		if (this.showSecurityCircuits) {
			this.observer.observe(this.$refs.securityCircuitsRef)
		} else {
			delete this.sections.securityCircuits
		}
		this.observer.observe(this.$refs.impulseCounterRef)
		if (this.showAkkumulatorSection) {
			this.observer.observe(this.$refs.akkumulatorRef)
		} else {
			delete this.sections.akkumulator
		}

		this.$emit('sections', this.sections)
	},

	methods: {
		fieldDynamicClasses(values, errors, valueKey) {
			const isInvalid = errors[valueKey]
			const isMatchRegister =
				(values ? values[valueKey] : undefined)
				== this.station.getMetric(valueKey)?.value

			return {
				'is-invalid': isInvalid,
				'not-match-register': !isMatchRegister && !isInvalid,
			}
		},

		async readRelatedRpc() {
			if (this.relatedRpcId == undefined) {
				return
			}

			this.relatedRpc = await this.readRpc(this.relatedRpcId)

			this.$emit('rpc', this.relatedRpc)
		},

		taskStateDescription(state) {
			const stateObj = this.rpcTaskSchema.state.findState(state)
			return stateObj == undefined || stateObj == false
				? "невизначено"
				: stateObj.title
		},

		// Delete date field if date is cleared by button "Clear"
		onChangeDate(key, newDate) {
			if (newDate == "") {
				this.$refs.form.setFieldValue(key, null);
			}
		},

		async onSubmit(values) {
			let settingsWriteResult = false
			let passportWriteResult = false
			let relatedRpcId = undefined

			delete values[this.$options.CLOCK_KEY]

			// Keys that need to be write to registers
			const settingsKeys = new Set([
				Station.passportKeys.serviceUnit.akkEmptyVoltage,
				Station.passportKeys.serviceUnit.akkFullVoltage,
				Station.passportKeys.serviceUnit.u220Min,
				Station.passportKeys.serviceUnit.u220Max,
				Station.passportKeys.serviceUnit.u220ThresholdHandler,
				Station.passportKeys.serviceUnit.u220Adjust,
				Station.passportKeys.serviceUnit.channel1InpMin,
				Station.passportKeys.serviceUnit.channel1InpMax,
				Station.passportKeys.serviceUnit.channel1ThresholdHandler,
				Station.passportKeys.serviceUnit.channel1Adjust,
				Station.passportKeys.serviceUnit.channel2InpMin,
				Station.passportKeys.serviceUnit.channel2InpMax,
				Station.passportKeys.serviceUnit.channel2ThresholdHandler,
				Station.passportKeys.serviceUnit.channel2Adjust,
				Station.passportKeys.serviceUnit.shuntMin,
				Station.passportKeys.serviceUnit.shuntMax,
				Station.passportKeys.serviceUnit.shuntThresholdHandler,
				Station.passportKeys.serviceUnit.shuntAdjust,
				Station.passportKeys.serviceUnit.securityCircuit_1_handler,
				Station.passportKeys.serviceUnit.securityCircuit_2_handler,
				Station.passportKeys.serviceUnit.securityCircuit_3_handler,
				Station.passportKeys.serviceUnit.securityCircuit_4_handler,
				Station.passportKeys.serviceUnit.securityCircuit_5_handler,
				Station.passportKeys.serviceUnit.securityCircuit_6_handler,
			])

			// Found changed values in settings
			const diff = getObjectDiff(
				this.formValues,
				values
			)
			let changedSettings = new Set()
			if (diff !== null) {
				const changedKeys = new Set(Object.keys(diff))
				changedSettings = settingsKeys.intersection(changedKeys)
			}
			
			// If there is any changes in settings		
			if (changedSettings.size > 0) {
				// Set changed settings to payload Object
				const payload = {}
				for (const setting of changedSettings) {
					payload[setting] = values[setting]
				}

				// Submit settings to server
				relatedRpcId = await this.submitSettings(payload)
				settingsWriteResult = relatedRpcId === false ? false : true
			} else {
				settingsWriteResult = true
			}

			if (settingsWriteResult) {
				// Submit passport to server
				passportWriteResult = await this.submitPassport(values, relatedRpcId)
			}

			return settingsWriteResult && passportWriteResult
		},

		async submitPassport(values, relatedRpcId=undefined) {
			const payload = {}//{ ...values }
			payload[Station.passportKeys.serviceUnit.self] = { ...values }
			delete payload[Station.passportKeys.serviceUnit.self][Station.passportKeys.measurePotentialOnOffStation.self]
			delete payload[Station.passportKeys.serviceUnit.self][Station.passportKeys.notificationDistributionLists.self]

			payload[Station.passportKeys.measurePotentialOnOffStation.self] =
				{ ...values[Station.passportKeys.measurePotentialOnOffStation.self] }
			// console.log("values", values)

			// Enumerate all schema and delete empty fields
			for (const [key, ] of Object.entries(this.schema.fields)) {
				// Delete all empty strings
				if (typeof values[key] == 'string' && values[key]?.length == 0) {
					payload[key] = null
				}

				// Delete all unefined fields
				if (payload[key] === undefined) {
					delete payload[key]
				}
			}
			for (const [key, value] of Object.entries(values)) {
				// Delete all empty strings
				if (value?.type == 'string' && value.length == 0) {
					delete payload[Station.passportKeys.serviceUnit.self][key]
				}

				// Delete all unefined fields
				if (payload[Station.passportKeys.serviceUnit.self][key] === undefined) {
					delete payload[Station.passportKeys.serviceUnit.self][key]
				}
			}

			// Parse notification_distribution_lists and convert to passport format
			payload[Station.passportKeys.notificationDistributionLists.self] = this.station.passport.get(Station.passportKeys.notificationDistributionLists.self)
			const userId = this.accountStore.userId
			for (const [key, value] of Object.entries(values[Station.passportKeys.notificationDistributionLists.self])) {
				// If notification is enable
				if (value) {
					// if no list at all
					if (payload[Station.passportKeys.notificationDistributionLists.self][key] === undefined) {
						payload[Station.passportKeys.notificationDistributionLists.self][key] = []
					}
					// If current user is not in distribution notifications list
					if (!payload[Station.passportKeys.notificationDistributionLists.self][key].includes(userId)) {
						payload[Station.passportKeys.notificationDistributionLists.self][key].push(userId)
					}
				} else {
					// If current user is in distribution notifications list
					if (payload[Station.passportKeys.notificationDistributionLists.self][key] !== undefined
						&& payload[Station.passportKeys.notificationDistributionLists.self][key].includes(userId)
					) {
						const index = payload[Station.passportKeys.notificationDistributionLists.self][key].indexOf(userId);
						payload[Station.passportKeys.notificationDistributionLists.self][key].splice(index, 1)
					}
				}
			}

			// Convert time fields to datetime for api compatibility
			//
			// measurePotentialOnOffStation
			let time = payload[Station.passportKeys.measurePotentialOnOffStation.self][Station.passportKeys.measurePotentialOnOffStation.startTime]
			if (time) {
				let date = new Date()
				date.setHours(time?.slice(0,2), time?.slice(3,5), 0, 0)
				payload[Station.passportKeys.measurePotentialOnOffStation.self][Station.passportKeys.measurePotentialOnOffStation.startTime] =
					convertToApiTimeDate(date)
			}

			payload[Station.passportKeys.updateBy] = this.accountStore.userId

			delete payload[Station.passportKeys.serviceUnit.self][Station.passportKeys.serviceUnit.pwrCntStart]

			// Save rpc id
			if (relatedRpcId) {
				payload[Station.passportKeys.serviceUnit.self][Station.passportKeys.serviceUnit.relatedRpc] = relatedRpcId
			}

			this.stationsStore.updatePasport(
				(result) => {
					if (result) {
						this.printHint('Паспорт успішно оновлено.', 'passport')
						// Update passport
						this.stationsStore.readPassport(undefined, this.stationId)
						// Update rpc with period
						this.rpcUpdateInterval = setInterval(async () => {
							this.readRelatedRpc()
						}, 1000)
					} else {
						this.printErr('Сервер відхилив запит.', 'passport')
					}
				},
				this.stationId,
				payload,
			)
		},

		async submitSettings(values) {
			// console.log("Submit settings", values)
			if (this.relatedRpc?.complete === false) {
				this.printErr(
					`Станція вже обробляє інший запит (${rpcTaskSchema.job.findJob(this.relatedRpc.job).title}). `
					+'Будь ласка дочекайтесь його виконання.',
				)

				return false
			} else if (!this.station.isActive) {
				this.printErr('Станція не активна. Тільки активні станції виконують запити.')
				return false
			} else if (this.relatedRpc === undefined
				|| this.relatedRpc.complete === true
			) {
				return await this.writeRpc(values)
			} else {
				this.printErr('Сталася невідома помилка')
				return false
			}
		},

		async writeRpc(values) {
			const context = {}
			context.settings = values
			
			context.unit = ServiceUnit.passportKeys.self
			context.writer = {
				"name": this.accountStore.userName,
				"id": this.accountStore.userId,
				"organization": {
					"name": this.accountStore.organization["title"],
					"id": this.accountStore.organization["_id"],
				},
			}

			return await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.setUnitRegisters.key,
				this.stationId,
				null,
				context,
			).then((result) => {
				if (result) {
					this.printHint('Запит надіслано. Спостерігайте за його станом.')
				} else {
					this.printErr('Сервер відхилив запит постановки завдання.')
				}

				return result
			})
		},

		submitButtonClick() {
			this.$refs.submitButtonRef.click()
		},

		printHint (msg) {
			this.feedbackPromtClass = "text-success"
			this.feedbackPromt = msg
		},

		printErr (msg) {
			this.feedbackPromtClass = "text-danger"
			this.feedbackPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		isFormEnabled() { return this.accountStore.permissions.includes('manage_stations') },

		relatedRpcId() {
			const serviceUnitPassport = this.station?.passport.get(Station.passportKeys.serviceUnit.self)
			return serviceUnitPassport ? serviceUnitPassport[Station.passportKeys.serviceUnit.relatedRpc] : undefined
		},

		pwrCounterValue() {
			let pwrCounterMetric = this.station?.getMetric(ServiceUnit.metricKeys.extPwrCounterEnergy, 'service')
			if (typeof pwrCounterMetric == 'object') {
				return pwrCounterMetric.value.toFixed(2)
			} else {
				return ''
			}
		},

		formValues() {
			const passport = Object.fromEntries(this.station?.passport)
			const serviceUnitPassport = this.station?.passport.get(Station.passportKeys.serviceUnit.self)
			const measurePotentialOnOffStation = { ...passport[Station.passportKeys.measurePotentialOnOffStation.self] }

			// Translate datetime to time fields
			//
			let time = measurePotentialOnOffStation?.start_time
			if (time) {
				time = parseApiTimeDate(time)
				measurePotentialOnOffStation.start_time =
					`${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}`
			}
			// Add clock field
			const date = new Date(Date.UTC(
				this.station.getMetric(ServiceUnit.metricKeys.clockYear)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockMonth)?.value - 1,
				this.station.getMetric(ServiceUnit.metricKeys.clockDay)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockHour)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockMinute)?.value,
				this.station.getMetric(ServiceUnit.metricKeys.clockSecond)?.value,
			))
			serviceUnitPassport[this.$options.CLOCK_KEY] = dateTimeLocalString(date)
			// console.log('date', serviceUnitPassport[this.$options.CLOCK_KEY])

			// If notification lists is present in passport
			let notificationsList = { ...passport[Station.passportKeys.notificationDistributionLists.self] }
			const hasUser = (listKey) => Array.isArray(notificationsList?.[listKey])
				&& notificationsList[listKey].includes(this.accountStore.userId)
			// console.log('notificationsList', notificationsList)
			if (notificationsList) {
				notificationsList = {
					[Station.passportKeys.notificationDistributionLists.potentialOutOfRange]: 
						hasUser(Station.passportKeys.notificationDistributionLists.potentialOutOfRange),
					[Station.passportKeys.notificationDistributionLists.u220]: 
						hasUser(Station.passportKeys.notificationDistributionLists.u220),
					[Station.passportKeys.notificationDistributionLists.channel_1]: 
						hasUser(Station.passportKeys.notificationDistributionLists.channel_1),
					[Station.passportKeys.notificationDistributionLists.channel_2]: 
						hasUser(Station.passportKeys.notificationDistributionLists.channel_2),
					[Station.passportKeys.notificationDistributionLists.shunt]: 
						hasUser(Station.passportKeys.notificationDistributionLists.shunt),
					[Station.passportKeys.notificationDistributionLists.securityCircuit1]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit1),
					[Station.passportKeys.notificationDistributionLists.securityCircuit2]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit2),
					[Station.passportKeys.notificationDistributionLists.securityCircuit3]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit3),
					[Station.passportKeys.notificationDistributionLists.securityCircuit4]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit4),
					[Station.passportKeys.notificationDistributionLists.securityCircuit5]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit5),
					[Station.passportKeys.notificationDistributionLists.securityCircuit6]: 
						hasUser(Station.passportKeys.notificationDistributionLists.securityCircuit6),
					
				}
			}

			return {
				...serviceUnitPassport,
				[Station.passportKeys.measurePotentialOnOffStation.self]: measurePotentialOnOffStation,
				[Station.passportKeys.notificationDistributionLists.self]: notificationsList,
			}
		},

		showAkkumulatorSection() {
			return [
				ServiceUnit.versions['2024-1'],
				ServiceUnit.versions['2024-2'],
				ServiceUnit.versions['2025-1'],
				ServiceUnit.versions['2025-2'],
				ServiceUnit.versions['2025-3'],
			].includes(this.formValues[ServiceUnit.passportKeys.unitVersion])
		},

		showSecurityCircuits() {
			return this.station.securityCircuitsNotificationNames.length > 0
		},
	},

	beforeUnmount() {
		if (this.rpcUpdateInterval != null) {
			clearInterval(this.rpcUpdateInterval)
			this.rpcUpdateInterval = null
		}

		// If observer, then disable
		if (this.observer) {
			this.observer.disconnect()
		}
	}
}
</script>




<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}

/* Style for input if it is not match register value */
.not-match-register {
	border-color: rgb(11, 94, 215);
	color: rgb(11, 94, 215);
}

.cur-value-promt {
	font-size: 0.875em;
}

.anchor-shift {
	position: relative;
	/* top: calc(v-bind(headerHeight) * -1); */
	top: -10px;
}
</style>